import { Component, OnInit } from '@angular/core';
import { Product } from 'src/app/model/product.model';
import { ProductService } from 'src/app/service/product.service';
import { compareNumbersOrStrings, compareVersions, dataFileNameFormatter, getReadableSubtype, numberFormatter, alphaNumericComparator } from '../utility-functions';
import { ColDef, ColGroupDef, GetContextMenuItemsParams, InitialGroupOrderComparatorParams, MenuItemDef } from 'ag-grid-community';


declare function showProductDetailModal(productToken: string): void;

@Component({
  selector: 'app-glazing-list',
  templateUrl: './glazing-list.component.html',
  styleUrls: ['./glazing-list.component.scss'],
  standalone: false
})
export class GlazingListComponent implements OnInit {

  /* PUBLIC PROPERTIES */

  public autoGroupColumnDef = {
    minWidth: 250,
    comparator: compareNumbersOrStrings,
  };
  public context = {
    this: this
  };
  public rowGroupPanelShow: 'always' | 'onlyWhenGrouping' | 'never' = 'always';
  public suppressDragLeaveHidesColumns = true;
  public rowSelection: 'single' | 'multiple' | undefined = 'single';
  public rowDeselection = true;
  public downloadingProduct = false;
  public downloadingProductDataFile = false;
  public statusBar = {
    statusPanels: [
      {
        statusPanel: 'agTotalRowCountComponent',
        align: 'left'
      },
      {
        statusPanel: 'agFilteredRowCountComponent'
      },
      {
        statusPanel: 'agSelectedRowCountComponent'
      }
    ]
  }


  public defaultColDef = {
    width: 100,
    sortable: true,
    resizable: true,
    filter: true
  };

  public columnDefs: (ColDef | ColGroupDef)[] = [
    {
      headerName: 'ID',
      hide: true,
      field: 'id',
      filter: 'agTextColumnFilter',
      width: 200
    },
    {
      headerName: 'Subtype',
      field: 'subtype',
      enableRowGroup: true,
      valueGetter: (params: any) => getReadableSubtype(params.data),
      headerClass: 'groupable-column',
      width: 220
    },
    {
      headerName: 'Manufacturer',
      field: 'manufacturer_name',
      enableRowGroup: true,
      rowGroup: true,
      headerClass: 'groupable-column',
      filter: 'agTextColumnFilter',
      comparator: alphaNumericComparator,
      width: 200,
    },
    {
      headerName: 'Name',
      field: 'name',
      filter: 'agTextColumnFilter',
      width: 200,
      comparator: alphaNumericComparator,
    },
    {
      headerName: 'Filename',
      field: 'data_file_name',
      valueGetter: dataFileNameFormatter,
      filter: 'agTextColumnFilter',
      comparator: alphaNumericComparator,
      width: 200
    },
    {
      headerName: 'NFRC ID',
      field: 'nfrc_id',
      filter: 'agNumberColumnFilter',
      width: 180
    },
    {
      headerName: 'CGDB ID',
      field: 'cgdb_id',
      filter: 'agNumberColumnFilter',
      width: 180
    },
    {
      headerName: 'Acceptance',
      field: 'acceptance',
      headerTooltip: '# = NFRC, @ = AERC',
      enableRowGroup: true,
      headerClass: 'groupable-column',
      filter: 'agTextColumnFilter',
      width: 150,
    },
    {
      headerName: 'Coating Name',
      field: 'coating_name',
      enableRowGroup: true,
      headerClass: 'groupable-column',
      filter: 'agTextColumnFilter',
      comparator: alphaNumericComparator,
      width: 150,
    },
    {
      headerName: 'TIR f',
      field: 'tir_front',
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated transmittance, in the infra-red spectrum, of the front of the layer',
      filter: 'agNumberColumnFilter',
      width: 150,
    },
    // {headerName: 'TIR b', field: 'tir_back', valueFormatter: numberFormatter },
    {
      headerName: 'Emis f',
      field: 'emissivity_front',
      headerTooltip: 'Emittance of the front surface of the layer',
      valueFormatter: numberFormatter,
      filter: 'agNumberColumnFilter',
      width: 150
    },
    {
      headerName: 'Emis b',
      field: 'emissivity_back',
      valueFormatter: numberFormatter,
      headerTooltip: 'Emittance of the back surface of the layer',
      filter: 'agNumberColumnFilter',
      width: 150
    },
    {
      headerName: 'tf_sol',
      field: 'tf_sol',
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated transmittance, in the solar spectrum, of the front of the layer',
      filter: 'agNumberColumnFilter',
      width: 150
    },
    {
      headerName: 'rf_sol',
      field: 'rf_sol',
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated reflectance, in the solar spectrum, of the front of the layer',
      filter: 'agNumberColumnFilter',
      width: 150
    },
    {
      headerName: 'rb_sol',
      field: 'rb_sol',
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated reflectance, in the solar spectrum, of the back of the layer',
      filter: 'agNumberColumnFilter',
      width: 150
    },
    {
      headerName: 'tf_vis',
      field: 'tf_vis',
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated transmittance, in the visible spectrum, of the front of the layer',
      filter: 'agNumberColumnFilter',
      width: 150
    },
    {
      headerName: 'rf_vis',
      field: 'rf_vis',
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated reflectance, in the visible spectrum, of the front of the layer',
      filter: 'agNumberColumnFilter',
      width: 150
    },
    {
      headerName: 'rb_vis',
      field: 'rb_vis',
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated reflectance, in the visible spectrum, of the back of the layer',
      filter: 'agNumberColumnFilter',
      width: 150
    },
    {
      headerName: 'Thickness (mm)',
      field: 'thickness',
      valueFormatter: numberFormatter,
      filter: 'agNumberColumnFilter',
      width: 150,
    },
    {
      headerName: 'tdw',
      field: 'tdw',
      valueFormatter: numberFormatter,
      headerTooltip: 'The “damage-weighted” transmittance of the layer',
      filter: 'agNumberColumnFilter',
      width: 150,
    },
    {
      headerName: 'tuv',
      field: 'tuv',
      valueFormatter: numberFormatter,
      headerTooltip: 'The integrated transmittance, in the ultra-violet spectrum, of the layer',
      filter: 'agNumberColumnFilter',
      width: 150,
    },
    {
      headerName: 'tspf',
      field: 'tspf',
      valueFormatter: numberFormatter,
      filter: 'agNumberColumnFilter',
      width: 150,
    },
    // {headerName: 'tkr', field: 'tkr' },
    {
      headerName: 'tf_ciex',
      field: 'tf_ciex',
      valueFormatter: numberFormatter,
      filter: 'agNumberColumnFilter',
      width: 150,
    },
    {
      headerName: 'tf_ciey',
      field: 'tf_ciey',
      valueFormatter: numberFormatter,
      filter: 'agNumberColumnFilter',
      width: 150,
    },
    {
      headerName: 'tf_ciez',
      field: 'tf_ciez',
      valueFormatter: numberFormatter,
      filter: 'agNumberColumnFilter',
      width: 150,
    },
    // {headerName: 'tf_r', field: 'tf_r' },
    // {headerName: 'tf_g', field: 'tf_g' },
    // {headerName: 'tf_b', field: 'tf_b' },
    {
      headerName: 'rf_ciex',
      field: 'rf_ciex',
      valueFormatter: numberFormatter,
      filter: 'agNumberColumnFilter',
      width: 150,
    },
    {
      headerName: 'rf_ciey',
      field: 'rf_ciey',
      valueFormatter: numberFormatter,
      filter: 'agNumberColumnFilter',
      width: 150,
    },
    {
      headerName: 'rf_ciez',
      field: 'rf_ciez',
      valueFormatter: numberFormatter,
      filter: 'agNumberColumnFilter',
      width: 150,
    },
    {
      headerName: 'Specular',
      field: 'is_specular',
      cellRenderer: (params: any) => {
        if (params.value === null || params.value === undefined) {
          return '';
        }
        return params.value ? 'Yes' : 'No';
      },
      enableRowGroup: true,
      headerClass: 'groupable-column',
      width: 150,
    },
    {
      headerName: 'Conductivity',
      field: 'conductivity',
      filter: 'agNumberColumnFilter',
      width: 150,
    },
    {
      headerName: 'Appearance',
      field: 'appearance',
      enableRowGroup: true,
      headerClass: 'groupable-column',
      filter: 'agTextColumnFilter',
      width: 150,
    },
    {
      headerName: 'Coated Side',
      field: 'coated_side',
      enableRowGroup: true,
      headerClass: 'groupable-column',
      filter: 'agTextColumnFilter',
      width: 150,
    },
    {
      headerName: 'IGDB Version',
      field: 'igdb_database_version',
      headerTooltip: '',
      enableRowGroup: true,
      headerClass: 'groupable-column',
      filter: 'agNumberColumnFilter',
      comparator: compareVersions,
      width: 150,
    },
    {
      headerName: 'Token',
      field: 'token',
      filter: 'agTextColumnFilter',
      width: 150,
      comparator: alphaNumericComparator,
    },

    {
      headerName: 'Decon.',
      field: 'deconstructable',
      width: 150,
    },
    // {headerName: 'rf_r', field: 'rf_r' },
    // {headerName: 'rf_g', field: 'rf_g' },
    // {headerName: 'rf_b', field: 'rf_b' },
    // {headerName: 'rbciex', field: 'rbciex', valueFormatter: numberFormatter },
    // {headerName: 'rbciey', field: 'rbciey', valueFormatter: numberFormatter },
    // {headerName: 'rbciez', field: 'rbciez', valueFormatter: numberFormatter },
    // {headerName: 'rb_r', field: 'rb_r' },
    // {headerName: 'rb_g', field: 'rb_g' },
    // {headerName: 'rb_b', field: 'rb_b' },
  ];

  sideBar = {

    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressPivots: true,
          suppressPivotMode: true,
        }
      },
      {
        id: 'filters',
        labelKey: 'filters',
        labelDefault: 'Filters',
        iconKey: 'menu',
        toolPanel: 'agFiltersToolPanel',
      }
    ],
    defaultToolPanel: '', // Collapses toolpanel
  };

  // Import to set this to null initially so
  // Ag-Grid shows 'loading' message.
  glazingList: Product[] | null = null;

  /* PRIVATE PROPERTIES */

  private gridApi: any;

  /* METHODS */

  constructor(private productService: ProductService) {
  }

  ngOnInit(): void {
    this.productService
      .getProductList(this.productService.TYPE_GLAZING)
      .subscribe((data: any) => (this.glazingList = data));
  }

  gridReady(params: any) {
    this.gridApi = params.api;
    //params.api.sizeColumnsToFit();
  }

  onSelectionChanged(event: any) {
    const selectedRows = this.gridApi.getSelectedRows();
    if (selectedRows.length === 1) {
      const selectedItem: Product = selectedRows[0];
      this.productService.selectedItem = selectedItem;
    } else {
      // Row was deselected (or more than one row, which shouldn't happen
      // since we should be on single select seting)
      this.productService.selectedItem = null;
    }
  }

  onRowDoubleClicked(event: any) {
    if (event.data) {
      const token = event.data.token;
      if (event.shiftKey) {
        const url = `/products/${token}`;
        window.open(url, '_blank');
      } else {
        showProductDetailModal(token);
      }
    }
  }

  downloadProduct(product: Product, format: string) {
    this.downloadingProduct = true;
    this.productService.openNewTabWithProduct(product, format);
  }

  downloadProductDataFile(product: Product) {
    this.downloadingProductDataFile = true;
    this.productService.openNewTabWithProductDataFile(product);
  }

  initialGroupOrderComparator(params: InitialGroupOrderComparatorParams) {
    const a = params.nodeA.key || "";
    const b = params.nodeB.key || "";
    return a < b ? -1 : a > b ? 1 : 0;
  };


  glazingListContextMenu(params: GetContextMenuItemsParams): MenuItemDef[] {

    if (params.node === null || params.node.data === null) {
      return [];
    }

    const product: Product = params.node.data;

    // Always show CSV and JSON download options
    const result: Array<MenuItemDef> = [
      {
        name: 'Open in new tab',
        action: () => {
          const url = `/products/${product.token}`;
          window.open(url, '_blank');
        },
      },
      {
        name: 'Download product CSV',
        action: () => {
          params.context.this.downloadProduct(product, 'csv');
        }
      },
      {
        name: 'View product JSON',
        action: () => {
          params.context.this.downloadProduct(product, 'json');
        }
      }
    ];

    // Decide whether to show data file download
    /*
    if (product.data_file_name) {
      // There's a filename, so we have to show a live or disabled download.
      // Either way, we *always* show the context menu item so the user doesn't get confused
      // if it doesn't appear.

      let shortDataFileName = product.data_file_name;
      if (shortDataFileName.length > 30) {
        shortDataFileName = `${shortDataFileName.substring(0, 30)}...`;
      }

      if (product.data_file_available) {
        result.push({
          name: `Download product datafile '${shortDataFileName}'`,
          action: () => {
            params.context.this.downloadProductDataFile(product);
          }
        });
      } else {
        result.push(
          {
            disabled: true,
            name: `Product datafile '${shortDataFileName}' unavailable for download`
          }
        );
      }
    }
     */
    return result;
  }


}

